import { type RequestFailure } from '@owl-nest/api-client'
import { type Address } from '@owl-nest/api-client/latest'
import { type AddressAction, TYPE } from './actions'
import { Status } from '../../status'

export type AddressState = {
  addresses: Address<string>[]
  status: {
    load: Status
    hasLoaded: boolean // whether a list of addresses has already been loaded
    add: Status
    remove: Status
    update: Status
  }
  failure?: RequestFailure
}

const initialState: AddressState = {
  addresses: [],
  status: {
    load: Status.PRISTINE,
    hasLoaded: false,
    add: Status.PRISTINE,
    remove: Status.PRISTINE,
    update: Status.PRISTINE,
  },
}

export function reducer(state: AddressState = initialState, action: AddressAction): AddressState {
  switch (action.type) {
    case TYPE.CREATE_REQUEST: {
      return {
        ...state,
        status: { ...state.status, add: Status.PENDING },
        failure: undefined,
      }
    }
    case TYPE.CREATE_FAILURE: {
      return {
        ...state,
        status: { ...state.status, add: Status.FAILURE },
        failure: action.error,
      }
    }
    case TYPE.CREATE_SUCCESS: {
      return {
        ...state,
        status: { ...state.status, add: Status.SUCCESS },
        addresses: [...state.addresses, action.success],
      }
    }

    case TYPE.REMOVE_REQUEST: {
      return {
        ...state,
        status: { ...state.status, remove: Status.PENDING },
        failure: undefined,
      }
    }
    case TYPE.REMOVE_FAILURE: {
      return {
        ...state,
        status: { ...state.status, remove: Status.FAILURE },
        failure: action.error,
      }
    }
    case TYPE.REMOVE_SUCCESS: {
      const removedAddressIndex = state.addresses.findIndex((address) => address.id === action.addressId)
      return {
        ...state,
        status: { ...state.status, remove: Status.SUCCESS },
        addresses: [
          ...state.addresses.slice(0, removedAddressIndex),
          ...state.addresses.slice(removedAddressIndex + 1),
        ],
      }
    }

    case TYPE.UPDATE_REQUEST: {
      return {
        ...state,
        status: { ...state.status, update: Status.PENDING },
        failure: undefined,
      }
    }
    case TYPE.UPDATE_FAILURE: {
      return {
        ...state,
        status: { ...state.status, update: Status.FAILURE },
        failure: action.error,
      }
    }
    case TYPE.UPDATE_SUCCESS: {
      const updatedAddressIndex = state.addresses.findIndex((address) => address.id === action.success.id)
      return {
        ...state,
        status: { ...state.status, update: Status.SUCCESS },
        addresses: [
          ...state.addresses.slice(0, updatedAddressIndex),
          action.success,
          ...state.addresses.slice(updatedAddressIndex + 1),
        ],
      }
    }

    case TYPE.LOAD_REQUEST: {
      return {
        ...state,
        status: { ...state.status, load: Status.PENDING },
        failure: undefined,
      }
    }
    case TYPE.LOAD_FAILURE: {
      return {
        ...state,
        status: { ...state.status, load: Status.FAILURE },
        failure: action.error,
      }
    }
    case TYPE.LOAD_SUCCESS: {
      return {
        ...state,
        status: { ...state.status, load: Status.SUCCESS, hasLoaded: true },
        addresses: action.success,
      }
    }
    default: {
      return state
    }
  }
}
